import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { getAdminWalletDetail, toggleAdminWallet } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function WalletDetail() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { userId } = useParams();
  const detail = useSelector((s) => s.profile.adminWalletDetail);

  const [toggling, setToggling] = useState(false);
  const [txSearch, setTxSearch] = useState("");
  const [txFilter, setTxFilter] = useState("all");

  useEffect(() => { dispatch(getAdminWalletDetail(userId)); }, [dispatch, userId]);

  const handleToggle = async () => {
    setToggling(true);
    await dispatch(toggleAdminWallet(userId));
    dispatch(getAdminWalletDetail(userId));
    setToggling(false);
  };

  if (!detail) {
    return (
      <div style={S.loading}>
        <div style={S.spinner} />
        <p style={{ color: "#888", marginTop: 16, fontSize: 14 }}>Loading wallet…</p>
      </div>
    );
  }

  const { wallet, profile } = detail;
  const allTx = wallet?.transactions || [];
  const filtered = allTx.filter((tx) => {
    const q = txSearch.toLowerCase();
    const matchSearch = !q ||
      (tx.narration || "").toLowerCase().includes(q) ||
      (tx.reference || "").toLowerCase().includes(q) ||
      (tx.sender?.accountName || "").toLowerCase().includes(q) ||
      (tx.recipient?.accountName || "").toLowerCase().includes(q);
    return matchSearch && (txFilter === "all" || tx.type === txFilter);
  });

  const totalCredit = allTx.filter((t) => t.type === "credit").reduce((s, t) => s + (t.amount || 0), 0);
  const totalDebit = allTx.filter((t) => t.type === "debit").reduce((s, t) => s + (t.amount || 0), 0);

  const fmtMoney = (k) => k != null ? `₦${(k / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const stats = [
    { label: "Account Number", value: wallet?.accountNumber || "—", icon: "bi bi-hash", accent: PRIMARY },
    { label: "Bank", value: wallet?.bankName || "—", icon: "bi bi-bank", accent: "#4fb0c6" },
    { label: "Status", value: wallet?.active ? "Active" : "Inactive", icon: "bi bi-circle-fill", accent: wallet?.active ? "#2ec4b6" : "#e63946" },
    { label: "KYC Tier", value: `Tier ${wallet?.tier || 1}`, icon: "bi bi-shield-check", accent: "#9b5de5" },
    { label: "BVN Verified", value: wallet?.bvnVerified ? "Yes" : "No", icon: "bi bi-person-check", accent: wallet?.bvnVerified ? "#2ec4b6" : "#aaa" },
    { label: "NIN Verified", value: wallet?.ninVerified ? "Yes" : "No", icon: "bi bi-fingerprint", accent: wallet?.ninVerified ? "#2ec4b6" : "#aaa" },
    { label: "Total Inflow", value: fmtMoney(totalCredit), icon: "bi bi-arrow-down-circle-fill", accent: "#2ec4b6" },
    { label: "Total Outflow", value: fmtMoney(totalDebit), icon: "bi bi-arrow-up-circle-fill", accent: "#e63946" },
    { label: "Transactions", value: allTx.length, icon: "bi bi-list-ul", accent: "#f4a261" },
  ];

  return (
    <div style={S.root}>
      <button style={S.backBtn} onClick={() => history.push("/admin/wallets")}>
        <i className="bi bi-arrow-left" style={{ marginRight: 6 }} />Back to Wallets
      </button>

      {/* Merchant header */}
      <div style={S.profileCard}>
        <div style={S.avatar}>
          <i className="bi bi-person-fill" style={{ fontSize: 28, color: PRIMARY }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: BG }}>{profile?.storeInfo?.storeName || profile?.fullname || "—"}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{profile?.email}</div>
        </div>
        <button
          style={{ border: `1.5px solid ${wallet?.active ? "#e63946" : "#2ec4b6"}`, background: wallet?.active ? "rgba(230,57,70,0.06)" : "rgba(46,196,182,0.06)", color: wallet?.active ? "#e63946" : "#2ec4b6", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
          onClick={handleToggle} disabled={toggling}
        >
          {toggling ? "…" : wallet?.active ? "Disable Wallet" : "Enable Wallet"}
        </button>
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        {stats.map((s) => (
          <div key={s.label} style={{ ...S.statCard, borderTop: `3px solid ${s.accent}` }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: s.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={s.icon} style={{ color: s.accent, fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: BG }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div style={S.txCard}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: BG, flex: 1 }}>Transactions</span>
          <input style={S.searchInput} placeholder="Search narration, ref…" value={txSearch} onChange={(e) => setTxSearch(e.target.value)} />
          {["all", "credit", "debit"].map((f) => (
            <button key={f} style={{ border: "1.5px solid #e0e0e0", borderRadius: 20, padding: "5px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, background: txFilter === f ? BG : "#fff", color: txFilter === f ? "#fff" : "#555" }} onClick={() => setTxFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Amount", "Type", "Narration", "Channel", "Counterparty", "Date", "Status"].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No transactions</td></tr>
              ) : filtered.map((tx, i) => {
                const isCredit = tx.type === "credit";
                const party = isCredit ? tx.sender : tx.recipient;
                return (
                  <tr key={tx.reference || i} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 700, color: isCredit ? "#2ec4b6" : "#e63946" }}>
                      {isCredit ? "+" : "−"}{fmtMoney(tx.amount)}
                    </td>
                    <td style={S.td}>
                      <span style={{ background: isCredit ? "rgba(46,196,182,0.12)" : "rgba(230,57,70,0.1)", color: isCredit ? "#2ec4b6" : "#e63946", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ ...S.td, maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tx.narration || "—"}</td>
                    <td style={S.td}>{tx.channel || "—"}</td>
                    <td style={S.td}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: BG }}>{party?.accountName || "—"}</div>
                      <div style={{ fontSize: 11, color: "#999" }}>{party?.bankName || ""}</div>
                    </td>
                    <td style={{ ...S.td, color: "#888" }}>{fmtDate(tx.paidAt)}</td>
                    <td style={S.td}>
                      <span style={{ background: tx.status === "success" ? "rgba(46,196,182,0.12)" : "#f5f5f5", color: tx.status === "success" ? "#2ec4b6" : "#aaa", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                        {tx.status || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360 },
  spinner: { width: 36, height: 36, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent" },
  backBtn: { background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 16px", cursor: "pointer", fontSize: 13, marginBottom: 22, display: "inline-flex", alignItems: "center", fontWeight: 500 },
  profileCard: { background: "#fff", borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  avatar: { width: 56, height: 56, borderRadius: "50%", background: "rgba(167,204,72,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginBottom: 24 },
  statCard: { background: "#fff", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  txCard: { background: "#fff", borderRadius: 10, padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  searchInput: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "10px 12px", fontSize: 13, color: "#555", verticalAlign: "middle" },
};
